import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "sahara_token";
export const REFRESH_KEY = "sahara_refresh";
export const IMP_TOKEN_KEY = "sahara_imp_token"; // super-admin token stored while impersonating

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));
export const getRefresh = () => localStorage.getItem(REFRESH_KEY);
export const setRefresh = (t) => (t ? localStorage.setItem(REFRESH_KEY, t) : localStorage.removeItem(REFRESH_KEY));

// Plain axios (no interceptor) for token refresh so we can send the refresh token explicitly.
export const rawApi = axios.create({ baseURL: API_BASE, withCredentials: true });

// Ensures a fresh httpOnly access-token cookie exists so same-origin file/PDF
// links opened in a new tab authenticate (they cannot send the Bearer header).
export async function ensureAuthCookie() {
  const rt = getRefresh();
  if (!rt) return;
  try {
    await rawApi.post("/auth/refresh", {}, { headers: { Authorization: `Bearer ${rt}` } });
  } catch { /* ignore */ }
}

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  // The JSON default above makes axios turn a FormData body into JSON (the file is lost and the
  // upload fails with 422), so file uploads must go as multipart; axios then adds the boundary.
  if (typeof FormData !== "undefined" && config.data instanceof FormData) config.headers["Content-Type"] = "multipart/form-data";
  return config;
});

let _refreshPromise = null;
async function _doRefresh() {
  const rt = getRefresh();
  if (!rt) return null;
  if (!_refreshPromise) {
    _refreshPromise = rawApi
      .post("/auth/refresh", {}, { headers: { Authorization: `Bearer ${rt}` } })
      .then((r) => {
        const t = r?.data?.access_token;
        if (t) setToken(t);
        return t || null;
      })
      .catch(() => null)
      .finally(() => { _refreshPromise = null; });
  }
  return _refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config || {};
    const url = original.url || "";
    const isAuthFlow = url.includes("/auth/refresh") || url.includes("/auth/login");
    if (status === 401 && !isAuthFlow && !original._retry) {
      original._retry = true;
      const newToken = await _doRefresh();
      if (newToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
      // refresh failed -> session truly over
      setToken(null);
      setRefresh(null);
      if (window.location.pathname !== "/login") window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export default api;

export const fmtINR = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
};

export const fmtINRDec = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 });
};

export const fmtMoney = (n, currency = "INR") => {
  const cur = (currency || "INR").toUpperCase();
  const v = Number(n || 0);
  try {
    return v.toLocaleString(cur === "INR" ? "en-IN" : "en-US", { style: "currency", currency: cur, minimumFractionDigits: 2 });
  } catch {
    return `${cur} ${v.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  }
};

export const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  const m = s.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(s);
  if (isNaN(d)) return s;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()} ${hh}:${mi}`;
};
